import { array, boolean, discriminatedUnion, enum as enumFactory, literal, number, strictObject, string, union } from "../../lazy-zod.js";
import { openMeldRequestContextSchema } from "./profile-identity.js";
import { daemonServiceLaneSchema } from "./service-contract.js";
import { localFolderPathSchema } from "./local-folder-path.js";
import { projectSlugSchema, repositoryFingerprintSchema } from "../http/projects.js";
//#region src/schemas/daemon/local-project-control.ts
const boundedIdentifierSchema = string().trim().min(1).max(512);
const clientGenerationSchema = number().int().nonnegative();
const localProjectControlAbsolutePathSchema = localFolderPathSchema;
const localProjectControlGitStatusSchema = enumFactory([
	"not_checked",
	"available",
	"not_installed",
	"command_line_tools_missing",
	"timed_out",
	"unavailable"
]);
const localProjectControlCandidateSchema = strictObject({
	candidateId: boundedIdentifierSchema,
	canonicalPath: localProjectControlAbsolutePathSchema,
	candidateKind: enumFactory(["repository", "worktree"]),
	repositoryFingerprint: repositoryFingerprintSchema
});
const localProjectControlConnectionSchema = strictObject({
	bindingGeneration: boundedIdentifierSchema,
	path: localProjectControlAbsolutePathSchema,
	isDefault: boolean(),
	status: enumFactory(["available", "missing"])
});
const localProjectControlDiscoveryBaseShape = {
	candidates: array(localProjectControlCandidateSchema),
	connections: array(localProjectControlConnectionSchema),
	projectId: boundedIdentifierSchema
};
const localProjectControlProjectDiscoverySchema = discriminatedUnion("status", [
	strictObject({
		...localProjectControlDiscoveryBaseShape,
		connections: array(localProjectControlConnectionSchema).min(1),
		status: literal("connected")
	}),
	strictObject({
		...localProjectControlDiscoveryBaseShape,
		connections: array(localProjectControlConnectionSchema).min(1),
		status: literal("connected_folder_missing")
	}),
	strictObject({
		...localProjectControlDiscoveryBaseShape,
		candidates: array(localProjectControlCandidateSchema).min(1),
		connections: array(localProjectControlConnectionSchema).max(0),
		status: literal("found")
	}),
	strictObject({
		...localProjectControlDiscoveryBaseShape,
		candidates: array(localProjectControlCandidateSchema).max(0),
		connections: array(localProjectControlConnectionSchema).max(0),
		status: literal("not_found")
	}),
	strictObject({
		...localProjectControlDiscoveryBaseShape,
		candidates: array(localProjectControlCandidateSchema).max(0),
		connections: array(localProjectControlConnectionSchema).max(0),
		status: literal("not_connected")
	}),
	strictObject({
		...localProjectControlDiscoveryBaseShape,
		candidates: array(localProjectControlCandidateSchema).max(0),
		connections: array(localProjectControlConnectionSchema).max(0),
		status: literal("verification_unavailable")
	})
]).superRefine((project, context) => {
	if (project.status !== "connected" && project.status !== "connected_folder_missing") return;
	const defaults = project.connections.filter((connection) => connection.isDefault);
	if (defaults.length !== 1) {
		context.addIssue({
			code: "custom",
			message: "A connected Project must have exactly one default connection.",
			path: ["connections"]
		});
		return;
	}
	const expectedDefaultStatus = project.status === "connected" ? "available" : "missing";
	if (defaults[0]?.status !== expectedDefaultStatus) context.addIssue({
		code: "custom",
		message: `The default connection must be ${expectedDefaultStatus} for ${project.status}.`,
		path: ["connections"]
	});
});
const localProjectControlDiscoverProjectsRequestSchema = strictObject({
	includeCandidates: boolean().optional(),
	expectedOrganizationId: boundedIdentifierSchema,
	clientGeneration: clientGenerationSchema,
	projectIds: array(boundedIdentifierSchema).min(1).max(500).optional()
});
const localProjectControlCanonicalProjectSchema = strictObject({
	projectId: boundedIdentifierSchema,
	repositoryFingerprint: repositoryFingerprintSchema.nullable()
});
const localProjectControlDiscoverProjectsDaemonRequestSchema = strictObject({
	includeCandidates: boolean().optional(),
	expectedOrganizationId: boundedIdentifierSchema,
	clientGeneration: clientGenerationSchema,
	projects: array(localProjectControlCanonicalProjectSchema).max(500)
});
const localProjectControlDiscoverProjectsResponseSchema = strictObject({
	requestId: boundedIdentifierSchema,
	clientGeneration: clientGenerationSchema,
	deviceId: boundedIdentifierSchema,
	checkedAtMs: number().int().nonnegative(),
	gitStatus: localProjectControlGitStatusSchema,
	projects: array(localProjectControlProjectDiscoverySchema).max(500)
});
const localProjectControlConnectBaseShape = {
	expectedOrganizationId: boundedIdentifierSchema,
	projectId: boundedIdentifierSchema,
	clientGeneration: clientGenerationSchema,
	idempotencyKey: boundedIdentifierSchema,
	moveConfirmationToken: boundedIdentifierSchema.optional()
};
const localProjectControlConnectCandidateRequestSchema = strictObject({
	...localProjectControlConnectBaseShape,
	candidateId: boundedIdentifierSchema,
	candidatePath: localProjectControlAbsolutePathSchema
});
const localProjectControlConnectDeclaredPathRequestSchema = strictObject({
	...localProjectControlConnectBaseShape,
	confirmNonGit: boolean().optional(),
	declaredPath: localProjectControlAbsolutePathSchema
});
const localProjectControlCanonicalConnectShape = {
	projectSlug: projectSlugSchema,
	repositoryFingerprint: repositoryFingerprintSchema.nullable()
};
const localProjectControlConnectCandidateDaemonRequestSchema = strictObject({
	...localProjectControlConnectBaseShape,
	...localProjectControlCanonicalConnectShape,
	candidateId: boundedIdentifierSchema,
	candidatePath: localProjectControlAbsolutePathSchema
});
const localProjectControlConnectDeclaredPathDaemonRequestSchema = strictObject({
	...localProjectControlConnectBaseShape,
	...localProjectControlCanonicalConnectShape,
	confirmNonGit: boolean().optional(),
	declaredPath: localProjectControlAbsolutePathSchema
});
const localProjectControlConnectResponseSchema = strictObject({
	status: enumFactory(["connected", "unchanged"]),
	bindingGeneration: boundedIdentifierSchema,
	isDefault: boolean(),
	canonicalPath: localProjectControlAbsolutePathSchema
});
const localProjectControlExpectedBindingGenerationSchema = boundedIdentifierSchema;
const localProjectControlMutationBaseShape = {
	expectedOrganizationId: boundedIdentifierSchema,
	projectId: boundedIdentifierSchema,
	clientGeneration: clientGenerationSchema,
	idempotencyKey: boundedIdentifierSchema
};
const localProjectControlSetDefaultConnectionRequestSchema = strictObject({
	...localProjectControlMutationBaseShape,
	targetBindingGeneration: boundedIdentifierSchema
});
const localProjectControlSetDefaultConnectionResponseSchema = strictObject({
	status: enumFactory(["updated", "unchanged"]),
	connections: array(localProjectControlConnectionSchema)
});
const localProjectControlRemoveConnectionRequestSchema = strictObject({
	...localProjectControlMutationBaseShape,
	bindingGeneration: boundedIdentifierSchema,
	replacementBindingGeneration: boundedIdentifierSchema.optional()
});
const localProjectControlRemoveConnectionResponseSchema = strictObject({
	status: enumFactory(["removed", "unchanged"]),
	connections: array(localProjectControlConnectionSchema)
});
const localProjectControlRemoveMissingConnectionsRequestSchema = strictObject(localProjectControlMutationBaseShape);
const localProjectControlRemoveMissingConnectionsResponseSchema = strictObject({
	removedCount: number().int().nonnegative(),
	connections: array(localProjectControlConnectionSchema)
});
const localProjectControlDiscoverProjectsRpcInputSchema = localProjectControlDiscoverProjectsRequestSchema.extend({ context: openMeldRequestContextSchema });
const localProjectControlConnectCandidateRpcInputSchema = localProjectControlConnectCandidateRequestSchema.extend({ context: openMeldRequestContextSchema });
const localProjectControlConnectDeclaredPathRpcInputSchema = localProjectControlConnectDeclaredPathRequestSchema.extend({ context: openMeldRequestContextSchema });
const localProjectControlSetDefaultConnectionRpcInputSchema = localProjectControlSetDefaultConnectionRequestSchema.extend({ context: openMeldRequestContextSchema });
const localProjectControlRemoveConnectionRpcInputSchema = localProjectControlRemoveConnectionRequestSchema.extend({ context: openMeldRequestContextSchema });
const localProjectControlRemoveMissingConnectionsRpcInputSchema = localProjectControlRemoveMissingConnectionsRequestSchema.extend({ context: openMeldRequestContextSchema });
const localProjectControlErrorCodeSchema = enumFactory([
	"invalid_request",
	"invalid_response",
	"path_not_found",
	"path_not_directory",
	"path_unreadable",
	"path_conflict",
	"fingerprint_mismatch",
	"git_repository_required",
	"needs_confirmation",
	"git_unavailable",
	"command_line_tools_missing",
	"candidate_unavailable",
	"binding_not_found",
	"binding_generation_stale",
	"replacement_required",
	"replacement_invalid",
	"default_missing_requires_replacement",
	"service_offline",
	"service_update_required",
	"timeout",
	"unsupported",
	"internal_error"
]);
const localProjectControlNonConflictErrorCodeSchema = localProjectControlErrorCodeSchema.exclude(["path_conflict"]);
const localProjectControlPathConflictResponseSchema = strictObject({
	status: literal("rejected"),
	code: literal("path_conflict"),
	message: string().trim().min(1).max(1024),
	conflictingProjectSlug: projectSlugSchema,
	conflictScope: enumFactory(["same_organization", "another_organization"]),
	moveConfirmationToken: boundedIdentifierSchema,
	moveImpact: enumFactory([
		"additional_connection",
		"default_connection",
		"only_connection"
	])
});
const localProjectControlErrorResponseSchema = union([
	localProjectControlPathConflictResponseSchema,
	strictObject({
		status: literal("rejected"),
		code: literal("path_conflict"),
		message: string().trim().min(1).max(1024),
		conflictingProjectSlug: projectSlugSchema
	}),
	strictObject({
		status: literal("rejected"),
		code: localProjectControlNonConflictErrorCodeSchema,
		message: string().trim().min(1).max(1024)
	})
]);
const localProjectControlDiscoverProjectsResultSchema = union([localProjectControlDiscoverProjectsResponseSchema, localProjectControlErrorResponseSchema]);
const localProjectControlConnectResultSchema = union([localProjectControlConnectResponseSchema, localProjectControlErrorResponseSchema]);
const localProjectControlConnectOutcomeSchema = union([localProjectControlConnectResponseSchema, localProjectControlPathConflictResponseSchema]);
const localProjectControlSetDefaultConnectionResultSchema = union([localProjectControlSetDefaultConnectionResponseSchema, localProjectControlErrorResponseSchema]);
const localProjectControlRemoveConnectionResultSchema = union([localProjectControlRemoveConnectionResponseSchema, localProjectControlErrorResponseSchema]);
const localProjectControlRemoveMissingConnectionsResultSchema = union([localProjectControlRemoveMissingConnectionsResponseSchema, localProjectControlErrorResponseSchema]);
const LOCAL_PROJECT_CONTROL_FRAME_VERSION = 1;
function defineRequestFrame(code, request) {
	return strictObject({
		v: literal(LOCAL_PROJECT_CONTROL_FRAME_VERSION),
		code: literal(code),
		payload: strictObject({
			requestId: boundedIdentifierSchema,
			request
		})
	});
}
function defineResultFrame(code, result) {
	return strictObject({
		v: literal(LOCAL_PROJECT_CONTROL_FRAME_VERSION),
		code: literal(code),
		payload: strictObject({
			requestId: boundedIdentifierSchema,
			result
		})
	});
}
const localProjectControlDiscoverProjectsRequestFrameSchema = defineRequestFrame("daemon.local_project_control.discover_projects", localProjectControlDiscoverProjectsDaemonRequestSchema);
const localProjectControlDiscoverProjectsResultFrameSchema = defineResultFrame("daemon.local_project_control.discover_projects_result", localProjectControlDiscoverProjectsResultSchema);
const localProjectControlConnectCandidateRequestFrameSchema = defineRequestFrame("daemon.local_project_control.connect_candidate", localProjectControlConnectCandidateDaemonRequestSchema);
const localProjectControlConnectCandidateResultFrameSchema = defineResultFrame("daemon.local_project_control.connect_candidate_result", localProjectControlConnectResultSchema);
const localProjectControlConnectDeclaredPathRequestFrameSchema = defineRequestFrame("daemon.local_project_control.connect_declared_path", localProjectControlConnectDeclaredPathDaemonRequestSchema);
const localProjectControlConnectDeclaredPathResultFrameSchema = defineResultFrame("daemon.local_project_control.connect_declared_path_result", localProjectControlConnectResultSchema);
const localProjectControlSetDefaultConnectionRequestFrameSchema = defineRequestFrame("daemon.local_project_control.set_default_connection", localProjectControlSetDefaultConnectionRequestSchema);
const localProjectControlSetDefaultConnectionResultFrameSchema = defineResultFrame("daemon.local_project_control.set_default_connection_result", localProjectControlSetDefaultConnectionResultSchema);
const localProjectControlRemoveConnectionRequestFrameSchema = defineRequestFrame("daemon.local_project_control.remove_connection", localProjectControlRemoveConnectionRequestSchema);
const localProjectControlRemoveConnectionResultFrameSchema = defineResultFrame("daemon.local_project_control.remove_connection_result", localProjectControlRemoveConnectionResultSchema);
const localProjectControlRemoveMissingConnectionsRequestFrameSchema = defineRequestFrame("daemon.local_project_control.remove_missing_connections", localProjectControlRemoveMissingConnectionsRequestSchema);
const localProjectControlRemoveMissingConnectionsResultFrameSchema = defineResultFrame("daemon.local_project_control.remove_missing_connections_result", localProjectControlRemoveMissingConnectionsResultSchema);
const localProjectControlDaemonFrameSchema = discriminatedUnion("code", [
	localProjectControlDiscoverProjectsRequestFrameSchema,
	localProjectControlDiscoverProjectsResultFrameSchema,
	localProjectControlConnectCandidateRequestFrameSchema,
	localProjectControlConnectCandidateResultFrameSchema,
	localProjectControlConnectDeclaredPathRequestFrameSchema,
	localProjectControlConnectDeclaredPathResultFrameSchema,
	localProjectControlSetDefaultConnectionRequestFrameSchema,
	localProjectControlSetDefaultConnectionResultFrameSchema,
	localProjectControlRemoveConnectionRequestFrameSchema,
	localProjectControlRemoveConnectionResultFrameSchema,
	localProjectControlRemoveMissingConnectionsRequestFrameSchema,
	localProjectControlRemoveMissingConnectionsResultFrameSchema
]);
const runtimeTransportLocalProjectControlTargetShape = {
	v: literal(LOCAL_PROJECT_CONTROL_FRAME_VERSION),
	ownerUserId: boundedIdentifierSchema,
	deviceId: boundedIdentifierSchema,
	lane: daemonServiceLaneSchema,
	requestId: boundedIdentifierSchema
};
const runtimeTransportLocalProjectControlDiscoverProjectsRequestSchema = strictObject({
	...runtimeTransportLocalProjectControlTargetShape,
	request: localProjectControlDiscoverProjectsDaemonRequestSchema
});
const runtimeTransportLocalProjectControlConnectCandidateRequestSchema = strictObject({
	...runtimeTransportLocalProjectControlTargetShape,
	request: localProjectControlConnectCandidateDaemonRequestSchema
});
const runtimeTransportLocalProjectControlConnectDeclaredPathRequestSchema = strictObject({
	...runtimeTransportLocalProjectControlTargetShape,
	request: localProjectControlConnectDeclaredPathDaemonRequestSchema
});
const runtimeTransportLocalProjectControlSetDefaultConnectionRequestSchema = strictObject({
	...runtimeTransportLocalProjectControlTargetShape,
	request: localProjectControlSetDefaultConnectionRequestSchema
});
const runtimeTransportLocalProjectControlRemoveConnectionRequestSchema = strictObject({
	...runtimeTransportLocalProjectControlTargetShape,
	request: localProjectControlRemoveConnectionRequestSchema
});
const runtimeTransportLocalProjectControlRemoveMissingConnectionsRequestSchema = strictObject({
	...runtimeTransportLocalProjectControlTargetShape,
	request: localProjectControlRemoveMissingConnectionsRequestSchema
});
//#endregion
export { localProjectControlCandidateSchema, localProjectControlCanonicalProjectSchema, localProjectControlConnectCandidateDaemonRequestSchema, localProjectControlConnectCandidateRequestFrameSchema, localProjectControlConnectCandidateRequestSchema, localProjectControlConnectCandidateResultFrameSchema, localProjectControlConnectCandidateRpcInputSchema, localProjectControlConnectDeclaredPathDaemonRequestSchema, localProjectControlConnectDeclaredPathRequestFrameSchema, localProjectControlConnectDeclaredPathRequestSchema, localProjectControlConnectDeclaredPathResultFrameSchema, localProjectControlConnectDeclaredPathRpcInputSchema, localProjectControlConnectOutcomeSchema, localProjectControlConnectResponseSchema, localProjectControlConnectResultSchema, localProjectControlConnectionSchema, localProjectControlDaemonFrameSchema, localProjectControlDiscoverProjectsDaemonRequestSchema, localProjectControlDiscoverProjectsRequestFrameSchema, localProjectControlDiscoverProjectsRequestSchema, localProjectControlDiscoverProjectsResponseSchema, localProjectControlDiscoverProjectsResultFrameSchema, localProjectControlDiscoverProjectsResultSchema, localProjectControlDiscoverProjectsRpcInputSchema, localProjectControlErrorCodeSchema, localProjectControlErrorResponseSchema, localProjectControlExpectedBindingGenerationSchema, localProjectControlGitStatusSchema, localProjectControlPathConflictResponseSchema, localProjectControlProjectDiscoverySchema, localProjectControlRemoveConnectionRequestFrameSchema, localProjectControlRemoveConnectionRequestSchema, localProjectControlRemoveConnectionResponseSchema, localProjectControlRemoveConnectionResultFrameSchema, localProjectControlRemoveConnectionResultSchema, localProjectControlRemoveConnectionRpcInputSchema, localProjectControlRemoveMissingConnectionsRequestFrameSchema, localProjectControlRemoveMissingConnectionsRequestSchema, localProjectControlRemoveMissingConnectionsResponseSchema, localProjectControlRemoveMissingConnectionsResultFrameSchema, localProjectControlRemoveMissingConnectionsResultSchema, localProjectControlRemoveMissingConnectionsRpcInputSchema, localProjectControlSetDefaultConnectionRequestFrameSchema, localProjectControlSetDefaultConnectionRequestSchema, localProjectControlSetDefaultConnectionResponseSchema, localProjectControlSetDefaultConnectionResultFrameSchema, localProjectControlSetDefaultConnectionResultSchema, localProjectControlSetDefaultConnectionRpcInputSchema, runtimeTransportLocalProjectControlConnectCandidateRequestSchema, runtimeTransportLocalProjectControlConnectDeclaredPathRequestSchema, runtimeTransportLocalProjectControlDiscoverProjectsRequestSchema, runtimeTransportLocalProjectControlRemoveConnectionRequestSchema, runtimeTransportLocalProjectControlRemoveMissingConnectionsRequestSchema, runtimeTransportLocalProjectControlSetDefaultConnectionRequestSchema };

//# sourceMappingURL=local-project-control.js.map